import React, { Suspense, useEffect, useState } from 'react'
import { AuthService } from 'services/authService'
import { MunicipalityService } from 'services/municipalityService'
import AuthError from 'common/components/AuthError'
import AuthLoading from 'common/components/AuthLoading'
import { useUser } from 'providers/UserProvider'
import { useMunicipality } from 'providers/MunicipalityProvider'
import { useSchool } from 'providers/SchoolProvider'
import { SchoolService } from 'services/schoolService'
import { RoleService } from 'services/roleService'
import { useHistory } from 'react-router-dom'
import User from 'data-models/user'
import ChunkLoadErrorBoundary from 'common/components/ChunkLoadErrorBoundary/ChunkLoadErrorBoundary'

const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp'))

export default function App() {
  const { user, setUser } = useUser()
  const [isLoggingIn, setIsLoggingIn] = useState(true)
  const [loginError, setLoginError] = useState<string | undefined>(undefined)
  const { municipality, setMunicipality, setMunicipalities } = useMunicipality()
  const { setSchools } = useSchool()
  const history = useHistory()
  const [showAuthError, setShowAuthError] = useState(false)
  const [mode, setMode] = useState('')

  useEffect(() => {
    async function initialLogin() {
      try {
        await AuthService.login()
        const user = (await AuthService.getUser()) as User | null
        if (!user?.role) throw new Error('You are not authorized to use this module!')
        else setUser(user)

        const municipality = await MunicipalityService.getMunicipality(user.organizationId)
        setMunicipality(municipality)

        if (RoleService.isSuperAdmin(user)) {
          const municipalities = await MunicipalityService.getMunicipalities()
          setMunicipalities(municipalities)
        }
      } catch (error: any | Error) {
        const errMsg = error instanceof Error ? error.message : error.response.statusText
        setLoginError(errMsg)
      } finally {
        setIsLoggingIn(false)
      }
    }
    initialLogin()
  }, [])
  useEffect(() => {
    if (user.role) {
      const storedMode = localStorage.getItem('appMode')
      if (storedMode) {
        setMode(storedMode)
      } else {
        const newMode = getDefaultMode(user)
        setMode(newMode)
        localStorage.setItem('appMode', newMode)
      }
    }
  }, [user])

  useEffect(() => {
    if (municipality.code) SchoolService.getSchoolsForCurrentUser(user, municipality).then(setSchools)
  }, [user, municipality])

  useEffect(() => {
    const unlisten = history.listen(() => {
      if (!AuthService.getToken()) {
        setShowAuthError(true)
      } else {
        setShowAuthError(false)
      }
    })

    // Clean up the listener when the component unmounts
    return () => {
      unlisten()
    }
  }, [history])

  if (isLoggingIn) return <AuthLoading />

  if (loginError || showAuthError) return <AuthError />

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback="Loading...">
        <AuthenticatedApp />
      </Suspense>
    </ChunkLoadErrorBoundary>
  )
}

const getDefaultMode = user => {
  switch (user.role) {
    case 'Grundskola.SuperAdmin':
    case 'Grundskola.MunicipalityAdmin':
    case 'Grundskola.MunicipalityUser':
      return 'standard'
    case 'Grundskola.SchoolAdmin':
    case 'Grundskola.SchoolUser':
      return 'standard'
    default:
      console.warn(`Unknown user role: ${user.role}. Using 'standard' mode.`)
      return 'standard'
  }
}
